import React from "react";
import _ from "underscore";
import API from "../lib/TimeEditAPI";
import GroupInformationPane, { TGroupReservation } from "./GroupInformationPane";
import Language from "../lib/Language";
import Log from "../lib/Log";
import { MillenniumDateTime } from "@timeedit/millennium-time";
import { Calendar } from "../models/Calendar";
import { Entry } from "../models/Entry";
import { EntryKind } from "../lib/EntryConstants";
import { Macros } from "../models/Macros";
import { Reservation } from "../models/Reservation";
import { Selection } from "../models/Selection";
import { TWithId } from "../types/models/TWithId";
import { TAppContextProp, withAppContext } from "../contexts/AppContext";

const GROUP_PANE_LIST_ID = 4096;
const MAX_EDITABLE_CLUSTER_DEPTH = 1;

const isEmptyGroup = (groups) => groups.length === 0 || groups[0] === 0;

const toEntry = (entryCluster) => {
    if (!entryCluster) {
        return null;
    }
    const result = _.clone(entryCluster);
    result.reservationids = [];
    result.objects = [];
    result.types = [];
    result.startTimes = entryCluster.begins.map((begin) => new MillenniumDateTime(begin));
    result.endTimes = entryCluster.ends.map((end) => new MillenniumDateTime(end));
    return result;
};

type TGroupPaneWrapperProps = {
    data: Selection;
    activeCalendar: Calendar;
    unlockedReservations: number[];
    temporaryUnlock: boolean;
    skipConfirmation: boolean;
    onEntryInfoOpen: (
        entry: Entry | Entry[] | number[],
        showPanel?: boolean,
        editMode?: boolean
    ) => void;
    endLockMode: () => void;
    setReservation: (reservationIds, callback?) => void;
} & TAppContextProp;

class GroupPaneWrapper extends React.Component<TGroupPaneWrapperProps> {
    onGroupReservationSelected = (groupEntry: TGroupReservation) => {
        if (!this.props.activeCalendar) {
            return;
        }
        const reservationIds = groupEntry.id ? [groupEntry.id] : [];
        const calendarGroupEntries = (this.props.activeCalendar.entries || []).filter(
            (etr) => etr.groups && _.some(etr.groups, (gr) => gr > 0)
        );

        const groups = [groupEntry?.group?.id];
        const matchingEntries = _.filter(
            calendarGroupEntries,
            (entry) =>
                _.isEqual(entry.groups, groups) && _.isEqual(entry.reservationids, reservationIds)
        );
        let bestEntry;
        if (matchingEntries.length > 0) {
            bestEntry = _.find(
                matchingEntries,
                (entry) =>
                    entry.kind === EntryKind.COMPLETE || entry.kind === EntryKind.GROUP_COMPLETE
            );
            if (!bestEntry) {
                bestEntry = matchingEntries[0];
            }
        }

        const headerObjects = _.isNullish(bestEntry) ? [] : bestEntry.headerObjects;

        this.props.setReservation(reservationIds);
        Reservation.get(reservationIds, (res) => {
            if (
                this.props.activeCalendar &&
                typeof this.props.activeCalendar.gotoReservation === "function"
            ) {
                this.props.appContext.update(
                    this.props.activeCalendar,
                    this.props.activeCalendar
                        .immutableSet({ currentEntryHeaderObjects: headerObjects })
                        .gotoReservation(res[0])
                );
            }
        });
    };

    // TODO entries are only entries for old reservation groups, for new groups, they're reservations
    onChangeTime = (reservationIds, entries, skipConfirmation = false) => {
        this.props.appContext.fireEvent(
            `groupPane`,
            Macros.Event.CHANGE_RESERVATION_TIME,
            reservationIds,
            skipConfirmation,
            false
        );
        this.props.setReservation(reservationIds, (selection) => {
            if (this.props.activeCalendar) {
                this.props.appContext.update(
                    this.props.activeCalendar,
                    this.props.activeCalendar.immutableSet({
                        spotlightDate:
                            entries && entries.length > 0
                                ? new MillenniumDateTime(
                                      entries[0].begin ? entries[0].begin : entries[0].begins[0]
                                  ).getMillenniumDate()
                                : null,
                    })
                );
            }
            this.props.appContext.update(
                this.props.data,
                selection.enableAddMode(selection.groups, true).disableEditMode()
            );
        });
    };

    onEditGroupReservation = (reservations: TWithId | TWithId[]) => {
        this.props.appContext.fireEvent(
            `groupPane`,
            Macros.Event.CHANGE_RESERVATION_OBJECTS,
            reservations
        );
    };

    toggleAddMode = (entries: Entry[]) => {
        const selection = this.props.data;
        const selectionGroup = this.props.activeCalendar
            ? this.props.activeCalendar.selectionGroup || []
            : [];
        const isEmpty = isEmptyGroup(selection.groups);
        if (selection.isGroupMode && selectionGroup.length > 0 && !isEmpty) {
            const reservationIds = _.flatten(selectionGroup.map((entry) => entry.reservationids));

            API.addReservationsToReservationGroup(
                selection.groups[0],
                reservationIds,
                (groupResult) => {
                    if (groupResult.details) {
                        Log.warning(Language.get("nc_could_not_add_to_reservation_group"));
                        return;
                    }
                    const newSelection = selection.immutableSet({
                        reservations: selection.reservations.concat(reservationIds),
                    });
                    this.props.appContext.update(
                        selection,
                        newSelection.toggleAddMode(newSelection.groups).disableEditMode()
                    );
                }
            );
        } else {
            if (!selection.isGroupMode) {
                const currentEntry = _.find(entries, (entry) =>
                    _.isEqual(entry.reservationids, selection.reservations)
                );
                this.props.appContext.update(
                    selection,
                    selection
                        .toggleAddMode(isEmpty ? [] : selection.groups)
                        .enableEditMode(toEntry(currentEntry), true)
                );
            } else {
                this.props.appContext.update(
                    selection,
                    selection.toggleAddMode(isEmpty ? [] : selection.groups).disableEditMode()
                );
            }
            if (this.props.activeCalendar) {
                this.props.appContext.update(
                    this.props.activeCalendar,
                    this.props.activeCalendar.immutableSet({ spotlightDate: null })
                );
            }
        }
    };

    finishMove = () => {
        const selection = this.props.data;
        this.props.appContext.update(
            selection,
            selection.toggleAddMode(selection.groups).disableEditMode()
        );
        if (this.props.activeCalendar) {
            this.props.appContext.update(
                this.props.activeCalendar,
                this.props.activeCalendar.immutableSet({
                    spotlightDate: null,
                })
            );
        }
        this.props.endLockMode();
    };

    render() {
        return (
            <li className="groupInformation">
                <GroupInformationPane
                    listId={GROUP_PANE_LIST_ID}
                    toggleAddMode={this.toggleAddMode}
                    clusterKind={this.props.data.clusterKind}
                    disableEntryEditing={
                        this.props.activeCalendar instanceof Calendar
                            ? this.props.activeCalendar.getMaxClusterDepth() >
                              MAX_EDITABLE_CLUSTER_DEPTH
                            : true
                    }
                    groupIds={this.props.data.getGroupIds()}
                    reservations={this.props.data.reservations}
                    onGroupReservationSelected={this.onGroupReservationSelected}
                    onEditGroupReservation={this.onEditGroupReservation}
                    onChangeTime={this.onChangeTime}
                    onInfoOpen={this.props.onEntryInfoOpen}
                    isGroupMode={this.props.data.isGroupMode}
                    unlockedReservations={this.props.unlockedReservations}
                    temporaryUnlock={this.props.temporaryUnlock}
                    skipConfirmation={this.props.skipConfirmation}
                    onFinishMoveButtonClicked={this.finishMove}
                />
            </li>
        );
    }
}

export default withAppContext(GroupPaneWrapper);
